export interface IBitwiseParser {
  set permissionList(permissionList: IPermission[]);
  set permissionSequence(permissionSeq: number[]);
  set permissionCode(code: string);
  parsePermission(): IPermissionFinal[];
}

export interface IPermission {
  permissionId: number;
  permissionName: string;
}

export interface IPermissionFinal extends IPermission {
  isEnabled: boolean;
}

export class BitWiseParser {
  private _parserVersion: string;

  constructor(parserVersion: string) {
    this._parserVersion = parserVersion;
  }

  public getParser(): IBitwiseParser {
    return new BitwiseParserFactory().getParserObj(this._parserVersion);
  }
}

class BitwiseParserFactory {
  public getParserObj(parserVersion: string): IBitwiseParser {
    switch (parserVersion) {
      case 'v1':
        return new ParserV1();
      case 'v2':
        return new ParserV2();
      default:
        throw new Error(`Invalid parser version: ${parserVersion}`);
    }
  }
}

class ParserV1 implements IBitwiseParser {
  private _permissionList: IPermission[] = [];
  private _permissionSeq: number[] = [];
  private _permissionCode: string = '';
  private _permissionIndexMap: Map<number, number> = new Map();

  set permissionList(permissionList: IPermission[]) {
    if (!Array.isArray(permissionList))
      throw new Error('Permission list must be an array');
    this._permissionList = permissionList;
  }

  set permissionSequence(permissionSeq: number[]) {
    if (!Array.isArray(permissionSeq))
      throw new Error('Permission sequence must be an array');
    this._permissionSeq = permissionSeq;
    this._permissionIndexMap.clear();
    permissionSeq.forEach((permissionId, index) => {
      this._permissionIndexMap.set(permissionId, index);
    });
  }

  set permissionCode(code: string) {
    if (typeof code !== 'string')
      throw new Error('Permission code must be a string');
    this._permissionCode = code.toLowerCase();
  }


parsePermission(): IPermissionFinal[] {
    this._validateInputs();

    const bits = this.convertHexToBits(this._permissionCode);
    const relevantBits = bits.slice(0, this._permissionSeq.length);

    return this._permissionList.map((permission) => {
      const bitIndex = this._permissionIndexMap.get(permission.permissionId);
      if (bitIndex === undefined)
        throw new Error(
          `Permission ID ${permission.permissionId} not found in permission sequence`
        );
      return {
        ...permission,
        isEnabled:
          bitIndex < relevantBits.length && relevantBits[bitIndex] === '1',
      };
    });
  }


  private _validateInputs(): void {
    if (this._permissionList.length === 0)
      throw new Error('Permission list cannot be empty');
    if (this._permissionSeq.length === 0)
      throw new Error('Permission sequence cannot be empty');
    if (!this._permissionCode)
      throw new Error('Permission code cannot be empty');

    const missingPermissions = this._permissionList.filter(
      (p) => !this._permissionIndexMap.has(p.permissionId)
    );
    if (missingPermissions.length > 0)
      throw new Error(
        `Permissions not found in sequence: ${missingPermissions
          .map((p) => p.permissionId)
          .join(', ')}`
      );
  }

  private convertHexToBits = (hex: string): string => {
    try {
      const decimalNumber = BigInt('0x' + hex);
      let bits = decimalNumber.toString(2);
      const requiredBitLength = hex.length * 4;
      bits = bits.padStart(requiredBitLength, '0');
      return bits;
    } catch {
      throw new Error(`Invalid hex code: ${hex}`);
    }
  };
}

class ParserV2 implements IBitwiseParser {
  private _permissionList: IPermission[] = [];
  private _permissionSeq: number[] = [];
  private _permissionCode: string = '';

  set permissionList(permissionList: IPermission[]) {
    this._permissionList = permissionList;
  }
  set permissionSequence(permissionSeq: number[]) {
    this._permissionSeq = permissionSeq;
  }
  set permissionCode(code: string) {
    this._permissionCode = code;
  }

  parsePermission(): IPermissionFinal[] {
    console.warn('ParserV2 is not implemented yet');
    return this._permissionList.map((permission) => ({
      ...permission,
      isEnabled: false,
    }));
  }
}
